/**
 * Workflow id validation and generation (LSEG-91).
 *
 * The admin API upserts workflow definitions via `PUT /workflows/{workflow_id}`,
 * so the id is client-chosen and becomes a URL path segment. An empty id turns
 * that request into `PUT /workflows/`, which matches the collection route and
 * comes back as an opaque 405 "Method Not Allowed" rather than a validation
 * error — the update handler is never reached. Users never author workflow ids
 * (the Builder's generator agent is not told the id convention either), so the
 * frontend must guarantee a valid id exists before any save.
 */

/** Longest name-derived slug prefix kept in a generated id. */
const GENERATED_ID_SLUG_MAX_LENGTH = 40;

/** Length of the random suffix appended to generated ids. */
const GENERATED_ID_SUFFIX_LENGTH = 8;

/**
 * Matches ids that are safe to embed as a URL path segment. Existing workflow
 * ids are snake_case (`chat_agent`, `workflow_generator`) with hyphens also in
 * use, so letters, digits, underscore and hyphen are permitted.
 */
const WORKFLOW_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

/** Unicode combining marks left behind by NFKD normalisation (diacritics). */
const COMBINING_MARKS_PATTERN = /[\u0300-\u036f]/g;

/** True when the id is a non-empty, URL-path-safe string. */
export const isValidWorkflowId = (id: string | null | undefined): id is string =>
  typeof id === "string" && WORKFLOW_ID_PATTERN.test(id);

/**
 * Generates a workflow id from the workflow's name: a snake_case slug plus a
 * short random suffix. The suffix is load-bearing, not cosmetic — the admin
 * API's PUT is an upsert, so two workflows that slug to the same name would
 * otherwise resolve to the same id and silently overwrite each other.
 */
export const generateWorkflowId = (name: string | null | undefined): string => {
  const slug = (name ?? "")
    .normalize("NFKD")
    .replace(COMBINING_MARKS_PATTERN, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, GENERATED_ID_SLUG_MAX_LENGTH)
    .replace(/_+$/, "");

  const suffix =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().replace(/-/g, "").slice(0, GENERATED_ID_SUFFIX_LENGTH)
      : Math.random()
          .toString(36)
          .slice(2, 2 + GENERATED_ID_SUFFIX_LENGTH);

  return `${slug || "workflow"}_${suffix}`;
};
